/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Renders the multilingual storefront sections authored in the owner-web Design editor
 * (Content + Sections tabs). Picks the text for the current UI language, falling back to
 * English then the first non-empty language.
 */

import { useTranslation } from 'react-i18next';
import { pickFromMap, type LocalizedTextMap } from '../utils/localized';

export interface SectionNote {
  picture?: { url?: string };
  symbol?: string;
  symbolForeColor?: string;
  foreColor?: string;
  backColor?: string;
  title?: LocalizedTextMap;
  caption?: LocalizedTextMap;
  text?: LocalizedTextMap;
}

export interface Section {
  backColor?: string;
  foreColor?: string;
  notesLayout?: 'vertical' | 'horizontal';
  alignment?: 'left' | 'right' | 'center';
  backgroundImage?: { url?: string };
  title?: LocalizedTextMap;
  description?: LocalizedTextMap;
  notes?: SectionNote[];
}

export function parseSections(json?: string): Section[] {
  if (!json || !json.trim()) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as Section[]) : [];
  } catch {
    return [];
  }
}

// Localized-text picker with storefront↔admin Chinese-code aliasing + English/any fallback.
const pick = (map: LocalizedTextMap | undefined, lang: string): string => pickFromMap(map, lang);

function hasContent(section: Section, lang: string): boolean {
  if (pick(section.title, lang) || pick(section.description, lang)) return true;
  return (section.notes || []).some(
    (n) => pick(n.title, lang) || pick(n.caption, lang) || pick(n.text, lang) || n.picture?.url || n.symbol,
  );
}

function NoteCard({ note, lang }: { note: SectionNote; lang: string }) {
  const title = pick(note.title, lang);
  const caption = pick(note.caption, lang);
  const text = pick(note.text, lang);
  const isSvg = note.symbol?.trim().startsWith('<svg');
  return (
    <div
      className="rounded-xl p-5 shadow-sm flex flex-col items-start"
      style={{ backgroundColor: note.backColor || 'rgba(255,255,255,0.04)', color: note.foreColor || 'inherit' }}
    >
      {note.picture?.url ? (
        <img src={note.picture.url} alt={title} className="h-14 w-14 object-contain mb-3" />
      ) : isSvg ? (
        <span
          className="inline-block h-10 w-10 mb-3"
          style={{ color: note.symbolForeColor || 'currentColor' }}
          dangerouslySetInnerHTML={{ __html: note.symbol as string }}
        />
      ) : null}
      {title && <h4 className="text-lg font-semibold mb-1">{title}</h4>}
      {caption && <p className="text-sm opacity-80 mb-1">{caption}</p>}
      {text && <p className="text-sm opacity-70 whitespace-pre-line">{text}</p>}
    </div>
  );
}

function SectionBlock({ section, lang }: { section: Section; lang: string }) {
  const title = pick(section.title, lang);
  const description = pick(section.description, lang);
  const notes = section.notes || [];
  const align = section.alignment || 'left';
  const alignClass = align === 'center' ? 'text-center items-center' : align === 'right' ? 'text-right items-end' : 'text-left items-start';
  const bg = section.backgroundImage?.url;

  return (
    <section
      className="relative w-full py-16 px-4"
      style={{
        backgroundColor: section.backColor || undefined,
        color: section.foreColor || undefined,
        ...(bg ? { backgroundImage: `url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
      }}
    >
      {bg && <div className="absolute inset-0 bg-black/40" />}
      <div className={`relative max-w-6xl mx-auto flex flex-col ${alignClass}`}>
        {title && <h2 className="text-3xl md:text-4xl font-bold mb-3">{title}</h2>}
        {description && <p className="text-lg opacity-80 max-w-3xl mb-8 whitespace-pre-line">{description}</p>}
        {notes.length > 0 && (
          <div
            className={
              section.notesLayout === 'horizontal'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 w-full'
                : 'flex flex-col gap-5 w-full max-w-3xl'
            }
          >
            {notes.map((note, i) => <NoteCard key={i} note={note} lang={lang} />)}
          </div>
        )}
      </div>
    </section>
  );
}

export default function SectionsRenderer({ json, startIndex = 0 }: { json?: string; startIndex?: number }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  // startIndex lets the page skip section(s) consumed elsewhere (e.g. section[0] feeds the hero).
  const sections = parseSections(json).slice(startIndex).filter((s) => hasContent(s, lang));
  if (sections.length === 0) return null;
  return <>{sections.map((s, i) => <SectionBlock key={i} section={s} lang={lang} />)}</>;
}
